import React, { useEffect, useState, useRef } from "react";
import { auth, db } from "../../firebase/config";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  orderBy,
  increment,
  onSnapshot,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { requireVerifiedEmail } from "../../utils/emailVerification";
import FarmAvatar from "../Common/FarmAvatar";
import {
  getMessageSenderProfile,
  isOfficialAdminMessage,
} from "./messageIdentity";


const Chat = ({ conversationId, currentUser, userProfile, initialMessage = "" }) => {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [typingUser, setTypingUser] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [userAvatarProfiles, setUserAvatarProfiles] = useState({});
  const [conversationMetadata, setConversationMetadata] = useState({});
const [reportingMessage, setReportingMessage] = useState(null);
const [reportReason, setReportReason] = useState("Harassment or abusive behavior");
const [reportDetails, setReportDetails] = useState("");
const [reportSubmitting, setReportSubmitting] = useState(false);

  const bottomRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  useEffect(() => {
    if (!conversationId) return;

    const unsub = onSnapshot(doc(db, "conversations", conversationId), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setConversationMetadata(data);
        setParticipants(data.users || []);
        setTypingUser(data.typing || null);
      }
    });

    return () => unsub();
  }, [conversationId]);

  useEffect(() => {
    const loadAvatarProfiles = async () => {
      if (!participants.length) return;

      const profiles = {};

      for (const uid of participants) {
        try {
          const userSnap = await getDoc(doc(db, "users", uid));

          if (userSnap.exists()) {
            profiles[uid] = { uid, ...userSnap.data() };
          } else {
            profiles[uid] = { uid };
          }
        } catch (err) {
          console.error("Error loading chat avatar profile:", err);
          profiles[uid] = { uid };
        }
      }

      setUserAvatarProfiles(profiles);
    };

    loadAvatarProfiles();
  }, [participants]);

  useEffect(() => {
    if (!conversationId || !currentUser?.uid) return;

    const q = query(
      collection(db, "conversations", conversationId, "messages"),
      orderBy("createdAt")
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const msgs = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      setMessages(msgs);

      try {
        await updateDoc(doc(db, "conversations", conversationId), {
          [`unreadCount.${currentUser.uid}`]: 0,
        });
      } catch (err) {
        console.error("Error resetting unread count:", err);
      }

      const unseenIncoming = msgs.filter(
        (msg) =>
          msg.senderId !== currentUser.uid &&
          !msg.seenBy?.includes(currentUser.uid)
      );

      for (const msg of unseenIncoming) {
        try {
          await updateDoc(
            doc(db, "conversations", conversationId, "messages", msg.id),
            {
              seenBy: [...(msg.seenBy || []), currentUser.uid],
            }
          );
        } catch (err) {
          console.error("Error updating seen receipt:", err);
        }
      }
    });

    return () => unsubscribe();
  }, [conversationId, currentUser]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (initialMessage && !text.trim()) {
      setText(initialMessage);
    }
  }, [initialMessage, conversationId, text]);

  const getAvatarProfile = (uid) =>
    userAvatarProfiles[uid] ||
    (uid === currentUser?.uid && userProfile ? { uid, ...userProfile } : { uid });

  const handleTyping = async (value) => {
    setText(value);

    if (!conversationId || !currentUser?.uid) return;

    const conversationRef = doc(db, "conversations", conversationId);

    try {
      await updateDoc(conversationRef, { typing: currentUser.uid });
    } catch (err) {
      console.error("Error updating typing state:", err);
    }

    clearTimeout(typingTimeoutRef.current);

    typingTimeoutRef.current = setTimeout(async () => {
      try {
        await updateDoc(conversationRef, { typing: null });
      } catch (err) {
        console.error("Error clearing typing state:", err);
      }
    }, 1500);
  };

  const sendMessage = async () => {
    if (!text.trim() || !conversationId || !currentUser?.uid) return;
    if (!requireVerifiedEmail(currentUser, userProfile)) return;

    const messageText = text.trim();
    const otherUserId = participants.find((id) => id !== currentUser.uid);

    try {
      await addDoc(collection(db, "conversations", conversationId, "messages"), {
        text: messageText,
        senderId: currentUser.uid,
        seenBy: [currentUser.uid],
        createdAt: serverTimestamp(),
      });

      await updateDoc(doc(db, "conversations", conversationId), {
        lastMessage: messageText,
        lastMessageAt: serverTimestamp(),
        lastSenderId: currentUser.uid,
        typing: null,
        [`unreadCount.${currentUser.uid}`]: 0,
        ...(otherUserId ? { [`unreadCount.${otherUserId}`]: increment(1) } : {}),
      });

      clearTimeout(typingTimeoutRef.current);
      setText("");
    } catch (err) {
      console.error("Error sending message:", err);
    }
  };

  const closeReportModal = () => {
  setReportingMessage(null);
  setReportReason("Harassment or abusive behavior");
  setReportDetails("");
  setReportSubmitting(false);
};

const submitMessageReport = async () => {
  if (!currentUser?.uid || !reportingMessage?.id) {
    alert("Missing report details.");
    return;
  }

  try {
    setReportSubmitting(true);

    const otherParticipantId =
      reportingMessage.senderId ||
      participants.find((id) => id !== currentUser.uid) ||
      null;

    await addDoc(collection(db, "supportRequests"), {
      requestKind: "report",
      type: "message",
      reportType: "message",

      reason: reportReason,
      details: reportDetails.trim(),

      conversationId,
      messageId: reportingMessage.id,
      targetId: reportingMessage.id,
      targetTitle: "Reported chat message",
      targetOwnerId: reportingMessage.senderId || null,

      reportedSenderId: reportingMessage.senderId || null,
      reportedMessageText: reportingMessage.text || "",
      reportedMessageImage:
        reportingMessage.imageUrl ||
        reportingMessage.fileUrl ||
        reportingMessage.attachmentUrl ||
        "",

      reporterId: currentUser.uid,
      reporterEmail: currentUser.email || auth.currentUser?.email || "",

      participants,

      status: "open",
      source: "message_report_button",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    alert("Message reported. Thank you for helping keep MyFarmingPal safe.");
    closeReportModal();
  } catch (error) {
    console.error("Failed submitting message report:", error);
    alert("Could not submit report: " + error.message);
    setReportSubmitting(false);
  }
};

  return (
    <div className="farm-chat-container">
      <div className="farm-chat-messages">
        {messages.map((msg, i) => {
          const isMe = msg.senderId === currentUser.uid;
          const isLast = i === messages.length - 1;
          const officialAdminMessage = isOfficialAdminMessage(
            msg,
            conversationMetadata
          );
          const senderProfile = getMessageSenderProfile({
            message: msg,
            conversation: conversationMetadata,
            senderProfile: getAvatarProfile(msg.senderId),
          });

          return (
            <div key={msg.id} className={`farm-chat-row ${isMe ? "me" : "them"}`}>
              {!isMe && (
                <FarmAvatar
                  profile={senderProfile}
                  className="farm-chat-animal"
                  showInitialBadge={false}
                />
              )}

              <div className="farm-chat-bubble">
  {!isMe && officialAdminMessage && (
    <strong style={{ display: "block", marginBottom: "4px" }}>
      MyFarmingPal Admin
    </strong>
  )}

  {msg.text && <div className="chat-message-text">{msg.text}</div>}

  {!isMe && !officialAdminMessage && (
    <button
      type="button"
      className="chat-report-message-btn"
      onClick={() => setReportingMessage(msg)}
    >
      🚩 Report
    </button>
  )}

  <div className="farm-chat-meta">
                  <span>
                    {msg.createdAt?.seconds &&
                      new Date(msg.createdAt.seconds * 1000).toLocaleTimeString(
                        [],
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                        }
                      )}
                  </span>

                  {isMe && isLast && msg.seenBy?.length > 1 && (
                    <span className="farm-chat-seen">✓ Seen</span>
                  )}
                </div>
              </div>

              {isMe && (
                <FarmAvatar
                  profile={senderProfile}
                  className="farm-chat-animal me-animal"
                  showInitialBadge={false}
                />
              )}
            </div>
          );
        })}

        <div ref={bottomRef} />
      </div>

      {typingUser && typingUser !== currentUser.uid && (
        <div className="farm-typing-row">
          <FarmAvatar
            profile={getAvatarProfile(typingUser)}
            className="farm-chat-animal"
            showInitialBadge={false}
          />
          <span className="farm-typing-dots">
            <i></i>
            <i></i>
            <i></i>
          </span>
          <span>typing...</span>
        </div>
      )}

{reportingMessage && (
  <div className="report-modal-backdrop" onClick={closeReportModal}>
    <div
      className="report-modal-card"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="report-modal-header">
        <div>
          <p className="eyebrow">Safety Report</p>
          <h3>Report this message</h3>
        </div>

        <button
          type="button"
          className="report-modal-close"
          onClick={closeReportModal}
        >
          ×
        </button>
      </div>

      <p className="report-target">
        <strong>Message:</strong>{" "}
        {reportingMessage.text
          ? reportingMessage.text.slice(0, 180)
          : "Attachment or file message"}
        {reportingMessage.text?.length > 180 ? "..." : ""}
      </p>

      <label className="form-label">Reason</label>
      <select
        value={reportReason}
        onChange={(e) => setReportReason(e.target.value)}
      >
        <option value="Harassment or abusive behavior">
          Harassment or abusive behavior
        </option>
        <option value="Spam or scam">Spam or scam</option>
        <option value="Unsafe or suspicious">Unsafe or suspicious</option>
        <option value="Fake account">Fake account</option>
        <option value="Inappropriate content">Inappropriate content</option>
        <option value="Other">Other</option>
      </select>

      <label className="form-label">Details optional</label>
      <textarea
        value={reportDetails}
        onChange={(e) => setReportDetails(e.target.value)}
        rows={4}
        placeholder="Tell us what happened..."
      />

      <div className="report-modal-actions">
        <button
          type="button"
          className="btn-soft"
          onClick={closeReportModal}
          disabled={reportSubmitting}
        >
          Cancel
        </button>

        <button
          type="button"
          className="btn-primary"
          onClick={submitMessageReport}
          disabled={reportSubmitting}
        >
          {reportSubmitting ? "Submitting..." : "Submit Report"}
        </button>
      </div>
    </div>
  </div>
)}

      <div className="farm-chat-input">
        <label className="sr-only" htmlFor="chat-message-input">
  Type a message
</label>
        
        <input
          id="chat-message-input"
          name="chatMessage"
          type="text"
          value={text}
          onChange={(e) => handleTyping(e.target.value)}
          placeholder="Type a message..."
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              sendMessage();
            }
          }}
        />

        <button onClick={sendMessage} disabled={!text.trim()}>
          ➤
        </button>
      </div>
    </div>
  );
};

export default Chat;
